import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Download, Send, Trash2, Search } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ClientQuotes() {
  const { t } = useLanguage();
  const [quotes, setQuotes] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [sending, setSending] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/quotes`, { withCredentials: true });
      setQuotes(res.data);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDownloadPDF = async (quoteId) => {
    try {
      const res = await axios.get(`${API}/api/quotes/${quoteId}/pdf`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `Devis_${quoteId}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) { toast.error('Erreur PDF'); }
  };

  const handleSendEmail = async (quoteId) => {
    setSending(quoteId);
    try {
      const res = await axios.post(`${API}/api/quotes/${quoteId}/send-email`, {}, { withCredentials: true });
      toast.success(res.data.message || t('quote_sent'));
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur envoi'); }
    finally { setSending(null); }
  };

  const handleDelete = async (quoteId) => {
    if (!window.confirm(t('confirm_delete_quote'))) return;
    try {
      await axios.delete(`${API}/api/quotes/${quoteId}`, { withCredentials: true });
      toast.success(t('quote_deleted'));
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const q = searchQuery.toLowerCase().trim();
  const filtered = q ? quotes.filter(quote =>
    (quote.quote_number || '').toLowerCase().includes(q) ||
    (quote.client_name || '').toLowerCase().includes(q) ||
    (quote.client_phone || '').toLowerCase().includes(q) ||
    (quote.client_email || '').toLowerCase().includes(q)
  ) : quotes;

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">{t('client_quotes_title')}</h1>
            <p className="text-sm text-slate-500 mt-1">{t('client_quotes_desc')}</p>
          </div>
          <Badge className="bg-slate-100 text-slate-700 text-sm">{filtered.length} / {quotes.length}</Badge>
        </div>

        <Card className="border-slate-200 shadow-sm">
          <CardHeader>
            <div className="flex items-center gap-3">
              <Search size={18} className="text-slate-400" />
              <Input
                data-testid="client-quotes-search"
                placeholder={t('search_quotes_placeholder')}
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="flex-1"
              />
            </div>
          </CardHeader>
          <CardContent>
            {filtered.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-8">{q ? t('no_search_results') : t('no_quotes')}</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>{t('quote_number')}</TableHead>
                    <TableHead>{t('quote_client')}</TableHead>
                    <TableHead>Commercial</TableHead>
                    <TableHead>Email / Tél</TableHead>
                    <TableHead>Date / Heure</TableHead>
                    <TableHead>{t('quote_total')}</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map(quote => (
                    <TableRow key={quote._id}>
                      <TableCell className="font-medium text-sm">{quote.quote_number}</TableCell>
                      <TableCell className="text-sm">{quote.client_name}</TableCell>
                      <TableCell className="text-sm text-slate-600">{quote.commercial_name || '—'}</TableCell>
                      <TableCell className="text-xs text-slate-500">
                        {quote.client_email && <div>{quote.client_email}</div>}
                        {quote.client_phone && <div>{quote.client_phone}</div>}
                        {!quote.client_email && !quote.client_phone && '—'}
                      </TableCell>
                      <TableCell className="text-xs text-slate-600">
                        <div>{new Date(quote.created_at || quote.date).toLocaleDateString('fr-FR')}</div>
                        <div className="text-slate-400">{new Date(quote.created_at || quote.date).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</div>
                      </TableCell>
                      <TableCell className="font-bold text-sm">{quote.total_ttc?.toFixed(2)} &euro;</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => handleDownloadPDF(quote._id)} title="PDF">
                            <Download size={14} />
                          </Button>
                          {quote.client_email && (
                            <Button size="sm" className="h-7 w-7 p-0 bg-blue-600 hover:bg-blue-700" disabled={sending === quote._id} onClick={() => handleSendEmail(quote._id)} title="Email">
                              <Send size={13} />
                            </Button>
                          )}
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => handleDelete(quote._id)} title="Supprimer">
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
